import './ScenariosList.css';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Input, message, Popconfirm, Switch, Table, Tooltip } from 'antd';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import ScenariosContext from '../../contexts/data/ScenariosContext';
import { SCENARIO_PAGE_PATH } from '../../pages/scenarios/ScenarioDetailPage';
import { DOMAINS_PAGE_PATH } from '../../pages/domains/DomainsPage';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import Scenario from '../../../domain/models/Scenario';
import PageScaffoldContext from '../../contexts/ui/PageScaffoldContext';
import { useNavigate } from 'react-router-dom';
import { advancedOnRow } from '../../../core/utils/commonUtils';

interface ScenariosListProps {
  domainId: string;
  subscriptionMode?: {
    idScenarios: string[];
    onToggleScenario: (scenarioId: string) => void;
  } | null;
}

const ScenariosList: React.FC<ScenariosListProps> = ({
  domainId,
  subscriptionMode,
}) => {
  const scenariosContext = useContext(ScenariosContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const pageScaffoldContentRef = pageScaffoldContext?.contentRef;
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [scenarios, setScenarios] = useState([] as Scenario[])

  useEffect(() => {
    if (
      scenariosContext.status === ContextStatus.IDLE ||
      scenariosContext.domainId !== domainId
    ) {
      scenariosContext.fetch(domainId);
    }

    return () => {
      scenariosContext.reset();
    };
  }, [domainId]);

  useEffect(() => {
    switch (scenariosContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        scenariosContext.fetch(domainId);
        message.success('Scénario supprimé avec succès');
        if (pageScaffoldContentRef?.current) {
          pageScaffoldContentRef.current.scrollTop = pageScaffoldContentRef.current.scrollHeight;
        }
        break;
      case ContextStatus.ERROR:
        message.error(scenariosContext.errorMessage);
        break;
      default:
        break;
    }
  }, [scenariosContext.deleteStatus]);

  useEffect(() => {
    switch (scenariosContext.duplicateStatus) {
      case ContextStatus.SUCCESS:
        scenariosContext.fetch(domainId);
        message.success('Scénario dupliqué avec succès');
        if (pageScaffoldContentRef?.current) {
          pageScaffoldContentRef.current.scrollTop = pageScaffoldContentRef.current.scrollHeight;
        }
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la duplication du scénario');
        break;
      default:
        break;
    }
  }, [scenariosContext.duplicateStatus]);

  const sortScenariosByActive = (scenarios: Scenario[]) => {
    // Copie avant tri : sort() trie en place, donc trier scenariosContext.scenarios
    // reordonnait le tableau du contexte pour tous ses autres consommateurs.
    return [...scenarios].sort((scenario1, scenario2) => {
      const scenario1Sort = subscriptionMode?.idScenarios.includes(scenario1.id) ? 1 : 0;
      const scenario2Sort = subscriptionMode?.idScenarios.includes(scenario2.id) ? 1 : 0;
      return scenario2Sort - scenario1Sort;
    });
  };

  useEffect(() => {
    if (scenariosContext.status === ContextStatus.SUCCESS) {
      if (subscriptionMode) {
        const scenariosSorted = sortScenariosByActive(scenariosContext.scenarios);
        // scenarios a quatre ecrivains (ici, la branche else, et les deux handlers de
        // toggle). Le deriver suppose d'unifier les quatre d'abord.
        // eslint-disable-next-line react/set-state-in-effect
        setScenarios(scenariosSorted);
      } else {
        setScenarios(scenariosContext.scenarios);
      }
    }
  }, [scenariosContext.scenarios, scenariosContext.status])

  const columnsBase = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
    },
  ];

  const columns =
    subscriptionMode != null
      ? [
        ...columnsBase,
        {
          title: 'Actif',
          dataIndex: 'id',
          key: 'toggle',
          render: (id: string) => {
            const checked = subscriptionMode.idScenarios.includes(id);
            return (
              <Switch
                checked={checked}
                onChange={(
                  checked: boolean,
                  e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
                ) => {
                  e.stopPropagation(); // Prevent row click
                  subscriptionMode.onToggleScenario(id);
                }}
              />
            );
          },
        }
      ]
      : [
        ...columnsBase,
        {
          title: '',
          key: 'action',
          render: (record: Scenario) => (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
              {renderDeleteButton(record)}
              {renderDuplicateButton(record)}
            </div>
          )
        }
      ];

  const renderDuplicateButton = (scenario: Scenario) => (
    <Tooltip title='Dupliquer'>
      <Button
        icon={<CopyOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          scenariosContext.submitDuplicate(scenario);
        }}
      />
    </Tooltip>
  );

  const renderDeleteButton = (scenario: Scenario) => (
    <Popconfirm
      title="Attention !"
      description="Supprimer le scénario ?"
      okText="Oui"
      cancelText="Non"
      onConfirm={(e) => {
        e?.stopPropagation();
        handleConfirmDeleteButton(scenario);
      }}
    >
      <Tooltip title='Supprimer' zIndex={1}>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </Tooltip>
    </Popconfirm>
  );


  const handleConfirmDeleteButton = (scenario: Scenario) => {
    scenariosContext.submitDelete(scenario);
  };

  const handleScenarioSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    if (event.target.value.length > 2) {
      handleScenarioSearch(event.target.value);
    }
    if (event.target.value === '') {
      setSearchInput('');
      setScenarios(scenariosContext.scenarios);
    }
  }

  const handleScenarioSearch = (value: string) => {
    const normalizedSearchInput = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    const scenariosFiltered = scenariosContext.scenarios.filter((scenario) =>
      scenario.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .includes(normalizedSearchInput)
    )
    setScenarios(scenariosFiltered);
  }

  return (
    <>
      <div className="scenarios-list__inputs">
        <Input.Search
          placeholder="Nom du scénario"
          value={searchInput}
          onChange={handleScenarioSearchChange}
          onSearch={handleScenarioSearch}
          disabled={
            scenariosContext.status ===
            ContextStatus.LOADING
          }
          loading={
            scenariosContext.status ===
            ContextStatus.LOADING
          }
        />
      </div>
      <Table
        loading={scenariosContext.status === ContextStatus.LOADING}
        columns={columns}
        dataSource={scenarios}
        rowKey={(record) => record.id}
        onRow={(record) =>
          advancedOnRow({
            url: `${DOMAINS_PAGE_PATH}/${domainId}${SCENARIO_PAGE_PATH}/${record.id}`,
            navigate,
            className: 'table-row',
          })
        }
        pagination={false}
      />
    </>
  );
};

export default ScenariosList;
